import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	MagnifyingGlassIcon,
	ChevronUpDownIcon,
	ChevronUpIcon,
	ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { GetAllLeads } from "../../Services/Dashboard/Leads";

const LeadsTable = () => {
	const [leads, setLeads] = useState([]);
	const [filtered, setFiltered] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);

	const [sortConfig, setSortConfig] = useState({
		key: "createdAt",
		direction: "desc",
	});

	const navigate = useNavigate();

	useEffect(() => {
		loadLeads();
	}, []);

	const loadLeads = async () => {
		setLoading(true);
		try {
			const res = await GetAllLeads();
			if (res.status === "Success") {
				const sorted = [...res.leads].sort(
					(a, b) => new Date(b.createdAt) - new Date(a.createdAt)
				);
				setLeads(sorted);
				setFiltered(sorted);
			}
		} catch (error) {
			console.error("Error fetching leads:", error);
		}
		setLoading(false);
	};

	const handleSearch = (value) => {
		setSearch(value);
		const newData = leads.filter(
			(x) =>
				x.insuredName.toLowerCase().includes(value.toLowerCase()) ||
				x.contactNumber.includes(value)
		);
		setFiltered(newData);
	};

	// Date Format
	const formatDate = (dateString) => {
		if (!dateString) return "-";

		const date = new Date(dateString);

		let formatted = date.toLocaleString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});

		return formatted.replace(/am|pm/, (match) => match.toUpperCase());
	};

	const sortBy = (key) => {
		let direction = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}

		const sortedData = [...filtered].sort((a, b) => {
			const first = key === "createdAt" ? new Date(a[key]) : a[key] || "";
			const second = key === "createdAt" ? new Date(b[key]) : b[key] || "";

			return direction === "asc"
				? first > second
					? 1
					: -1
				: first < second
				? 1
				: -1;
		});

		setSortConfig({ key, direction });
		setFiltered(sortedData);
	};

	const sortIcon = (key) => {
		if (sortConfig.key !== key)
			return <ChevronUpDownIcon className="w-4 inline ml-1" />;
		return sortConfig.direction === "asc" ? (
			<ChevronUpIcon className="w-4 inline ml-1" />
		) : (
			<ChevronDownIcon className="w-4 inline ml-1" />
		);
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow-md border-b sticky top-0 z-50">
				<div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
					<h2 className="text-xl font-bold text-gray-800">Customer Leads</h2>

					<div className="flex items-center gap-3">
						<select
							className="border rounded-lg px-3 py-2 text-sm"
							onChange={(e) => {
								const v = e.target.value;
								if (v === "") return setFiltered(leads);
								setFiltered(leads.filter((x) => x.interestedInsurance === v));
							}}
						>
							<option value="">All Insurance</option>
							<option value="Griha">Griha</option>
							<option value="Hospicash">Hospicash</option>
						</select>

						<div className="relative w-56">
							<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-500" />
							<input
								type="text"
								value={search}
								onChange={(e) => handleSearch(e.target.value)}
								className="pl-10 pr-4 py-2 border rounded-lg text-sm w-full focus:ring-primary focus:border-primary"
								placeholder="Search leads..."
							/>
						</div>

						<button
							onClick={() => navigate("/")}
							className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold shadow hover:shadow-md"
						>
							+ New Lead
						</button>
					</div>
				</div>
			</header>

			<main className="max-w-7xl mx-auto px-6 py-6">
				<div className="bg-white border shadow-lg rounded-2xl overflow-hidden">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
								<th className="p-4 text-center w-12">#</th>

								<th
									className="p-4 text-left cursor-pointer"
									onClick={() => sortBy("insuredName")}
								>
									Name {sortIcon("insuredName")}
								</th>

								<th
									className="p-4 text-left cursor-pointer"
									onClick={() => sortBy("contactNumber")}
								>
									Mobile {sortIcon("contactNumber")}
								</th>

								<th
									className="p-4 text-left cursor-pointer"
									onClick={() => sortBy("createdAt")}
								>
									Created At {sortIcon("createdAt")}
								</th>

								<th className="p-4 text-left">Insurance</th>

								<th className="p-4 text-center w-36">Actions</th>
							</tr>
						</thead>

						<tbody>
							{loading && (
								<tr>
									<td colSpan="6" className="py-10 text-center text-gray-500">
										Loading leads...
									</td>
								</tr>
							)}

							{!loading && filtered.length === 0 && (
								<tr>
									<td colSpan="6" className="py-10 text-center text-gray-500">
										No leads found
									</td>
								</tr>
							)}

							{!loading &&
								filtered.map((lead, index) => (
									<tr
										key={lead.leadUID}
										className="hover:bg-blue-50/50 transition"
									>
										<td className="p-4 text-center text-gray-500">
											{index + 1}
										</td>
										<td className="p-4 font-semibold text-gray-800">
											{lead.title} {lead.insuredName}
										</td>
										<td className="p-4">{lead.contactNumber}</td>
										<td className="p-4 text-gray-600 whitespace-nowrap">
											{formatDate(lead.createdAt)}
										</td>
										<td className="p-4">
											<span
												className={`px-3 py-1 rounded-full text-xs font-semibold ${
													lead.interestedInsurance === "Griha"
														? "bg-blue-100 text-blue-700"
														: "bg-green-100 text-green-700"
												}`}
											>
												{lead.interestedInsurance}
											</span>
										</td>
										<td className="p-4 text-center">
											<div className="flex justify-center gap-3">
												<button
													onClick={() => {
														if (lead.interestedInsurance === "Griha") {
															navigate(
																`/Griha/Quotation/Customer-Details/${lead.leadUID}`
															);
														} else if (
															lead.interestedInsurance === "Hospicash"
														) {
															navigate(
																`/Hospicash/Quotation/Customer-Details/${lead.leadUID}`
															);
														}
													}}
													className="text-primary underline text-xs font-semibold"
												>
													View
												</button>
												<button
													onClick={() => navigate(`/Lead/Edit/${lead.leadUID}`)}
													className="px-3 py-1 bg-primary text-white text-xs font-semibold rounded-lg shadow-sm hover:shadow"
												>
													Edit
												</button>
											</div>
										</td>
									</tr>
								))}
						</tbody>
					</table>
				</div>
			</main>
		</div>
	);
};

export default LeadsTable;
